import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SlideOverDrawer from '../common/SlideOverDrawer';
import { getFormConfig } from '../../forms';
import { useTenant } from '../../contexts/TenantContext';
import SafeIcon from '../../common/SafeIcon';
import { PDFOptimizer } from '../../lib/pdf/PDFOptimizer';
import * as FiIcons from 'react-icons/fi';

const {
  FiFileText,
  FiEdit,
  FiDownload,
  FiPrinter,
  FiCopy,
  FiTrash2,
  FiClock,
  FiUser,
  FiHome,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiExternalLink,
  FiShare2
} = FiIcons;

/**
 * CertificateQuickViewDrawer
 * Deep inspection slide-over drawer featuring:
 * - Embedded PDF Live Preview / download trigger
 * - Customer & Site Property Summary Cards
 * - Audit Log Action Timeline (Created, Edited, Status, Exported)
 * - Rapid action toolbar (Open Editor, Duplicate, Print, Status Switch)
 */
export const CertificateQuickViewDrawer = ({
  certificate,
  isOpen,
  onClose,
  onStatusChange,
  onDelete
}) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'pdf' | 'timeline'
  const navigate = useNavigate();
  const { activeTenant } = useTenant();

  if (!certificate) return null;

  const formConfig = getFormConfig(certificate.formId);
  const formData = certificate.data || {};
  const customerInfo = formData.customerInfo || formData.clientDetails || {};
  const jobAddress = formData.jobAddress || formData.siteAddress || formData.propertyDetails || {};
  const engineerInfo = formData.engineerDetails || formData.inspector || {};

  const handleOpenEditor = () => {
    navigate(`/forms/${certificate.formId}/${certificate.id}`);
    onClose();
  };

  const handleDuplicate = () => {
    navigate(`/forms/${certificate.formId}`, {
      state: { duplicateData: certificate.data }
    });
    onClose();
  };

  // Build synthetic audit timeline
  const timelineEvents = [
    {
      id: 1,
      type: 'created',
      title: 'Certificate Initialized',
      timestamp: certificate.createdAt ? new Date(certificate.createdAt).toLocaleString() : 'Recently',
      actor: engineerInfo.name || 'Engineer',
      icon: FiPlusCircleDefault,
      color: 'text-blue-500 bg-blue-50 dark:bg-blue-950/40'
    },
    {
      id: 2,
      type: 'edited',
      title: 'Inspection Data Updated',
      timestamp: certificate.updatedAt ? new Date(certificate.updatedAt).toLocaleString() : 'Recently',
      actor: engineerInfo.name || 'Engineer',
      icon: FiEdit,
      color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-950/40'
    },
    ...(certificate.status === 'completed'
      ? [
          {
            id: 3,
            type: 'signed',
            title: 'Engineer Signed & Completed',
            timestamp: new Date(certificate.updatedAt).toLocaleString(),
            actor: engineerInfo.name || 'Certified Engineer',
            icon: FiCheckCircle,
            color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
          }
        ]
      : [])
  ];

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={certificate.instanceId || `${formConfig?.name || 'Certificate'} #${certificate.id}`}
      subtitle={`${formConfig?.name || certificate.formId} • Organization: ${activeTenant.name}`}
      badge={certificate.status?.toUpperCase()}
      width="max-w-3xl"
      footer={
        <div className="w-full flex items-center justify-between">
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to permanently remove this certificate record?')) {
                onDelete?.(certificate.id);
                onClose();
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
            <span>Delete</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDuplicate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
              <span>Duplicate</span>
            </button>
            <button
              onClick={handleOpenEditor}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-sm transition-all"
            >
              <SafeIcon icon={FiEdit} className="w-3.5 h-3.5" />
              <span>Open in Editor</span>
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 gap-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-2.5 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Overview & Details
          </button>
          <button
            onClick={() => setActiveTab('pdf')}
            className={`pb-2.5 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'pdf'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Live Document Preview
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`pb-2.5 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'timeline'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Audit Trail & History
          </button>
        </div>

        {/* Tab 1: Overview & Details */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Quick Status Pill Action */}
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center gap-2.5">
                <SafeIcon
                  icon={certificate.status === 'completed' ? FiCheckCircle : FiClock}
                  className={`w-5 h-5 ${certificate.status === 'completed' ? 'text-emerald-500' : 'text-amber-500'}`}
                />
                <div>
                  <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
                    Status: {certificate.status === 'completed' ? 'Completed & Certified' : 'Draft in Progress'}
                  </h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Last modified: {new Date(certificate.updatedAt).toLocaleDateString()} at{' '}
                    {new Date(certificate.updatedAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              {onStatusChange && (
                <select
                  value={certificate.status || 'draft'}
                  onChange={(e) => onStatusChange(certificate.id, e.target.value)}
                  className="text-xs font-medium rounded-lg px-2.5 py-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 shadow-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              )}
            </div>

            {/* Cards Grid: Client, Property, Engineer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer Info Card */}
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="flex items-center gap-2 mb-3 text-slate-900 dark:text-slate-100 font-semibold text-xs">
                  <SafeIcon icon={FiUser} className="w-4 h-4 text-blue-500" />
                  <span>Customer / Landlord</span>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {customerInfo.name || customerInfo.customerName || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {customerInfo.telephone || customerInfo.phone || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[160px]">
                      {customerInfo.email || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Site Address Card */}
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="flex items-center gap-2 mb-3 text-slate-900 dark:text-slate-100 font-semibold text-xs">
                  <SafeIcon icon={FiHome} className="w-4 h-4 text-indigo-500" />
                  <span>Site Property Address</span>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Address:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[160px]">
                      {jobAddress.line1 || jobAddress.addressLine1 || jobAddress.address || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Town/City:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {jobAddress.town || jobAddress.city || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Postcode:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {jobAddress.postcode || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Engineer & Accreditation Box */}
            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <div className="flex items-center gap-2 mb-3 text-slate-900 dark:text-slate-100 font-semibold text-xs">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-emerald-500" />
                <span>Engineer & Accreditation Credentials</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                <div>
                  <span className="text-slate-500 block text-[11px]">Engineer Name</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    {engineerInfo.name || engineerInfo.engineerName || 'Default Certified Engineer'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[11px]">Gas Safe Reg / ID</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    {engineerInfo.gasSafeNo || engineerInfo.idCardNo || activeTenant.gasSafeRegNumber || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[11px]">Inspection Date</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    {formData.date || new Date(certificate.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Form Payload Summary Data */}
            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <h4 className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Certificate Payload Snapshot
              </h4>
              <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-lg font-mono text-[11px] text-slate-600 dark:text-slate-400 max-h-40 overflow-y-auto">
                <pre>{JSON.stringify(formData, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Live PDF Preview */}
        {activeTab === 'pdf' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiFileText} className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-900 dark:text-blue-200 font-medium">
                  Compliance Document Engine ({formConfig?.name})
                </span>
              </div>
              <button
                onClick={handleOpenEditor}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-semibold"
              >
                <span>Edit & Export</span>
                <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
              </button>
            </div>

            {formConfig?.pdfComponent ? (
              <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-950 p-4 text-center">
                <p className="text-xs text-slate-500 mb-3">
                  Open the form editor to view full reactive interactive PDF stream or trigger direct export:
                </p>
                <button
                  onClick={handleOpenEditor}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold shadow-md inline-flex items-center gap-2"
                >
                  <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                  <span>Launch PDF Viewer & Print Engine</span>
                </button>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400 text-xs">
                No PDF document generator mapped for this certificate schema.
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Timeline & Audit Trail */}
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
              Immutable Action History
            </h4>
            <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
              {timelineEvents.map((evt) => (
                <div key={evt.id} className="relative flex items-start gap-3">
                  <div
                    className={`absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900 ${evt.color}`}
                  >
                    <SafeIcon icon={evt.icon} className="w-2.5 h-2.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                      {evt.title}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      by <span className="font-medium text-slate-700 dark:text-slate-300">{evt.actor}</span> • {evt.timestamp}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SlideOverDrawer>
  );
};

const FiPlusCircleDefault = (props) => <FiFileText {...props} />;

export default CertificateQuickViewDrawer;
